import { TextContentBlockType, NavigationCollectionType } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { CirrusReaderPublicationLandmarkType } from "./CirrusReaderModel";
import type { IAttributeData } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import type { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import type { 
    ContentBlockClass, 
    ContainerContentBlockType, 
    MediaContentBlockType, 
    IContentBlock, 
    IContentBlockMarkData 
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { 
    ICirrusReaderLandmarkData, 
    ICirrusReaderNavigationItem, 
    ICirrusReaderNavigationTreeData, 
    ICirrusReaderReadingProgressionTimeline 
} from "./CirrusReaderModel";

export class CirrusReaderLandmark {
    private _attributes: IAttributeData[];
    private _blockClass: ContentBlockClass;
    private _blockType: ContainerContentBlockType | MediaContentBlockType | TextContentBlockType;
    private _children: IContentBlock[];
    private _description: string | undefined;
    private _documentIndex: number | undefined;
    private _id: number;
    private _marks: IContentBlockMarkData[];
    private _resourceHrefs: string[] | null;
    private _textContent: string | undefined;
    private _timelinePosition?: number
    private _serializedData: ICirrusReaderLandmarkData | undefined;
    private _navigationItem: ICirrusReaderNavigationItem | undefined;
    private _locator: ILocator | undefined;
    private _locatorUrl: string | undefined;
    private _subType: CirrusReaderPublicationLandmarkType | undefined;

    constructor(private _contentBlock: IContentBlock, private _navigationTree?: ICirrusReaderNavigationTreeData, private _timeline?: ICirrusReaderReadingProgressionTimeline) {
        const description = this._getDescriptionAttributeFromContentBlock(this._contentBlock, 'aria-label') || this._getDescriptionFromContentBlockStructure(this._contentBlock) || undefined;
        const documentsArray = this._contentBlock.getContentLocation().getReaderDocuments();
        const documentIndex = documentsArray[0] ? documentsArray[0].getIndexInSpine() : undefined;

        this._textContent = this._contentBlock.getTextContent();
        this._id = this._contentBlock.getId();
        this._documentIndex = documentIndex;
        this._resourceHrefs = this._contentBlock.getResourceHrefs();
        this._attributes = this._contentBlock.getAttributes();
        this._marks = this._contentBlock.getMarks();
        this._description = description;
        this._children = this._contentBlock.getChildren();
        this._blockType = this._contentBlock.getBlockType();
        this._blockClass = this._contentBlock.getBlockClass();

        // Let's see if this ContentBlock is also a footnote or endnote

        const hasFootnoteAttribute = this.attributes.some((attribute: IAttributeData) => {
            return (attribute.localName === 'role' && attribute.value === 'doc-footnote')
                || (attribute.localName === 'role' && attribute.value === 'doc-endnote')
                || (attribute.localName === 'type' && attribute.value === 'footnote')
                || (attribute.localName === 'type' && attribute.value === 'endnote')
                || (attribute.localName === 'type' && attribute.value === 'rearnote');

        });

        if (hasFootnoteAttribute) {
            this.subType = CirrusReaderPublicationLandmarkType.FOOTNOTE
        }
    }

    public get id(): number {
        return this._id;
    };

    public get attributes(): IAttributeData[] {
        return this._attributes;
    };

    public get blockClass(): ContentBlockClass {
        return this._blockClass;
    }

    public get blockType(): ContainerContentBlockType | MediaContentBlockType | TextContentBlockType {
        return this._blockType;
    }

    public get children(): IContentBlock[] {
        return this._children;
    }

    public get description(): string | undefined {
        return this._description;
    }

    public get documentIndex(): number | undefined {
        return this._documentIndex;
    }

    public get marks(): IContentBlockMarkData[] {
        return this._marks;
    }

    public get resourceHrefs(): string[] | null {
        return this._resourceHrefs;
    }

    public get subType(): CirrusReaderPublicationLandmarkType | undefined {
        return this._subType;
    }

    public set subType(subType: CirrusReaderPublicationLandmarkType | undefined) {
        this._subType = subType;
    }

    public get textContent(): string | undefined {
        return this._textContent;
    }


    public get locatorUrl(): string | undefined {

        if(this._locatorUrl !== undefined) {
            return this._locatorUrl;
        }
        this._locatorUrl = this._contentBlock.getLocator().toUrl().toString();
        return this._locatorUrl;
    }


    getLocator(charOffset?: number, charLength?:number): ILocator {

        if(this._locator !== undefined) {
            return this._locator;
        }

        this._locator = this._contentBlock.getLocator(charOffset, charLength);

        return this._locator;
    }

    async fetchTimelinePosition(): Promise<number| undefined> {

        if(this._timelinePosition !== undefined) {
            return this._timelinePosition;
        }

        this._timelinePosition = await this._timeline?.fetchTimelinePosition(this._contentBlock.getLocator());
        return this._timelinePosition;
    }

    fetchNavigationItem(): Promise<ICirrusReaderNavigationItem | undefined> {
        return this._fetchNavigationItem();
    }

    isBefore(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().isBefore(contentBlock.getContentLocation());
    }

    isAfter(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().isAfter(contentBlock.getContentLocation());
    }

    contains(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().contains(contentBlock.getContentLocation());
    }

    async serialize(createLocators?: boolean, recursive?: boolean): Promise<ICirrusReaderLandmarkData> {

        if(this._serializedData !== undefined) {
            return this._serializedData;
        }

        const contentBlockData =  this._contentBlock.toSerializableData({createLocators, recursive});
        this._serializedData = {
            timelinePosition: (await this.fetchTimelinePosition()),
            id: this.id,
            parentId: this._contentBlock.getParent()?.getId() || 0,
            attributes: this.attributes,
            marks: this.marks,
            resourceHrefs: this.resourceHrefs,
            description: this.description,
            navigationItem: (await this.fetchNavigationItem()),
            children: contentBlockData.children,
            locator: this.getLocator(),
            textContent: this.textContent || '',
            blockClass: this.blockClass,
            blockType: this.blockType,
            subType: this.subType,
            documentIndex: this.documentIndex,
            locatorUrl: this.getLocator().toUrl().toString(),
        };

        return this._serializedData;
    }

    private async _fetchNavigationItem(): Promise<ICirrusReaderNavigationItem | undefined> {

        if(this._navigationItem !== undefined) {
            return this._navigationItem;
        }

        const colibrioNavigationItemRefs = await this._contentBlock.getContentLocation().fetchNavigationItemReferences({
            collectionTypes: [NavigationCollectionType.TOC],
            greedy: true
        });
        let navigationItem: ICirrusReaderNavigationItem | undefined = undefined;
        const itemsInRange = colibrioNavigationItemRefs.getItemsInRange();
        if(itemsInRange[0]) {
            const locator: string | undefined = itemsInRange[0].getNavigationItem().getLocator()?.toUrl().toString();
            if(locator) {
                navigationItem = this._navigationTree?.items.find((item:  ICirrusReaderNavigationItem)=>{
                    return item.locator === locator;
                });
            }
        }

        this._navigationItem = navigationItem;

        return navigationItem;
    }

    private _getDescriptionFromContentBlockStructure(block: IContentBlock): string | undefined {
        const childTextBlock = block.getChildren().filter(block => block.getBlockType() === TextContentBlockType.TEXT);
        if(childTextBlock.length === 0) {
            return;
        }
        return childTextBlock.map(block => block.getTextContent()).join(' ').trim();
    }

    private _getDescriptionAttributeFromContentBlock(
        block: IContentBlock,
        preferredAttributeName: string | undefined = undefined
    ): string | undefined {
        const blockAttributes = block.getAttributes();
        let descAttribute: string | undefined = undefined;

        if (preferredAttributeName) {
            const attr = blockAttributes.find((attr: IAttributeData) => {
                return attr.localName == preferredAttributeName;
            });
            if (attr) {
                return attr.value;
            }
        }

        blockAttributes.forEach((attr: IAttributeData) => {
            switch (attr.localName) {
                case 'title':
                    descAttribute = attr.value;
                    break;
                case 'alt':
                    descAttribute = attr.value;
                    break;
                case 'aria-label':
                    descAttribute = attr.value;
                    break;
            }
        });

        return descAttribute;
    }

}
